import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Pencil, X, FlipHorizontal2, FlipVertical2, Type, Gauge, MonitorPlay } from "lucide-react";

export default function Teleprompter({ pendingScript, clearPending }) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("entry"); // entry | reading
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(6); // 1-50
  const [fontSize, setFontSize] = useState(52);
  const [mirrorH, setMirrorH] = useState(false);
  const [mirrorV, setMirrorV] = useState(false);

  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const accRef = useRef(0);

  // Enter from Susun Script → autofill + jump straight to reading mode
  useEffect(() => {
    if (pendingScript && pendingScript.trim()) {
      setText(pendingScript);
      setMode("reading");
      setPlaying(false);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      clearPending?.();
    }
    // eslint-disable-line
  }, [pendingScript]); // eslint-disable-line

  // Smooth auto-scroll loop
  useEffect(() => {
    if (mode !== "reading" || !playing) {
      cancelAnimationFrame(rafRef.current);
      lastTsRef.current = 0;
      return;
    }
    const step = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      const el = scrollRef.current;
      if (el) {
        const pxPerSec = speed * 4; // x1 ≈ 4px/s (sangat pelan) → x50 ≈ 200px/s
        accRef.current += pxPerSec * dt;
        if (accRef.current >= 1) {
          const whole = Math.floor(accRef.current);
          accRef.current -= whole;
          el.scrollTop += whole;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
            setPlaying(false);
            return;
          }
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [mode, playing, speed]);

  const startReading = () => {
    if (!text.trim()) return;
    setMode("reading");
    setPlaying(false);
    accRef.current = 0;
    requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; });
  };

  const reset = () => {
    accRef.current = 0;
    lastTsRef.current = 0;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const backToEntry = () => {
    setPlaying(false);
    setMode("entry");
  };

  // ---------- Reading mode (fullscreen) ----------
  if (mode === "reading") {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="teleprompter-reading">
        {/* Scrolling text */}
        <div ref={scrollRef} data-testid="teleprompter-scroll" className="flex-1 overflow-y-auto no-scrollbar">
          <div
            className="mx-auto max-w-4xl px-6 sm:px-10 text-center text-white font-semibold whitespace-pre-wrap"
            style={{
              fontSize: `${fontSize}px`,
              lineHeight: 1.4,
              paddingTop: "45vh",
              paddingBottom: "60vh",
              transform: `scaleX(${mirrorH ? -1 : 1}) scaleY(${mirrorV ? -1 : 1})`,
            }}
          >
            {text}
          </div>
        </div>

        {/* Center reading guide line */}
        <div className="pointer-events-none absolute left-0 right-0 top-[45vh] flex justify-center">
          <div className="h-[2px] w-full max-w-4xl bg-sk-brand/40" />
        </div>

        {/* Controls */}
        <div className="shrink-0 bg-neutral-950/95 border-t border-white/10 backdrop-blur px-4 sm:px-6 py-3">
          <div className="mx-auto max-w-4xl flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPlaying((p) => !p)}
                data-testid="teleprompter-playpause"
                className="h-11 w-11 rounded-full bg-sk-brand text-sk-ink flex items-center justify-center hover:bg-sk-brandDark transition-colors"
                title={playing ? "Jeda" : "Mulai"}
              >
                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </button>
              <button
                onClick={reset}
                data-testid="teleprompter-reset"
                className="h-11 w-11 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:border-sk-brand hover:text-sk-brand transition-colors"
                title="Ulang dari awal"
              >
                <RotateCcw className="h-5 w-5" />
              </button>
            </div>

            {/* Speed */}
            <div className="flex items-center gap-2 min-w-[150px] flex-1">
              <Gauge className="h-4 w-4 text-white/50 shrink-0" />
              <input
                type="range" min="1" max="50" step="1" value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
                data-testid="teleprompter-speed"
                className="w-full accent-[#DA6C46]"
              />
              <span className="text-xs text-white/60 w-9 text-right tabular-nums">x{speed}</span>
            </div>

            {/* Font size */}
            <div className="flex items-center gap-2 min-w-[150px] flex-1">
              <Type className="h-4 w-4 text-white/50 shrink-0" />
              <input
                type="range" min="24" max="96" step="2" value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                data-testid="teleprompter-fontsize"
                className="w-full accent-[#DA6C46]"
              />
              <span className="text-xs text-white/60 w-9 text-right tabular-nums">{fontSize}</span>
            </div>

            {/* Mirror + edit + exit */}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setMirrorH((m) => !m)}
                data-testid="teleprompter-mirror-h"
                className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold border transition-colors ${
                  mirrorH ? "border-sk-brand text-sk-brand bg-sk-brand/10" : "border-white/20 text-white/70 hover:text-white"
                }`}
                title="Cermin horizontal"
              >
                <FlipHorizontal2 className="h-4 w-4" /> <span className="hidden sm:inline">Mirror H</span>
              </button>
              <button
                onClick={() => setMirrorV((m) => !m)}
                data-testid="teleprompter-mirror-v"
                className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold border transition-colors ${
                  mirrorV ? "border-sk-brand text-sk-brand bg-sk-brand/10" : "border-white/20 text-white/70 hover:text-white"
                }`}
                title="Cermin vertikal"
              >
                <FlipVertical2 className="h-4 w-4" /> <span className="hidden sm:inline">Mirror V</span>
              </button>
              <button
                onClick={backToEntry}
                data-testid="teleprompter-edit"
                className="flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold border border-white/20 text-white/70 hover:text-white transition-colors"
                title="Ganti teks"
              >
                <Pencil className="h-4 w-4" /> <span className="hidden sm:inline">Ganti teks</span>
              </button>
              <button
                onClick={backToEntry}
                data-testid="teleprompter-exit"
                className="h-9 w-9 rounded-full border border-white/20 text-white/70 flex items-center justify-center hover:text-white transition-colors"
                title="Keluar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Entry mode (theme cream/terracotta) ----------
  return (
    <div>
      <div className="mb-6">
        <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Baca lancar</p>
        <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Teleprompter</h1>
        <p className="text-sk-muted mt-2 max-w-xl leading-relaxed">Tempel atau tulis naskahmu, atur kecepatan & ukuran teks, lalu baca tanpa kehilangan tatapan ke kamera. Bisa juga langsung dari hasil Susun Script.</p>
      </div>

      <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm">
        <label className="block text-sm font-semibold text-sk-cream mb-2">Naskah</label>
        <textarea
          data-testid="teleprompter-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          placeholder="Tempel atau tulis naskahmu di sini"
          className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent leading-relaxed"
        />
        <div className="flex items-center justify-between gap-4 mt-4">
          <p className="text-xs text-sk-muted">{text.trim() ? `${text.trim().split(/\s+/).length} kata` : "Belum ada teks"}</p>
          <button
            onClick={startReading}
            disabled={!text.trim()}
            data-testid="teleprompter-start-btn"
            className="flex items-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold px-6 py-3 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sk-brandDark disabled:opacity-60 disabled:hover:translate-y-0 shadow-lg shadow-sk-brand/20"
          >
            <MonitorPlay className="h-4 w-4" /> Mulai
          </button>
        </div>
      </div>
    </div>
  );
}
